import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { useFieldControl } from './Field';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  /** Secondary line under the label, for the consequence of ticking it. */
  hint?: ReactNode;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, className, id, disabled, ...rest },
  ref,
) {
  const field = useFieldControl(id);
  return (
    // The whole row is the target. A bare 16px box is a quarter of the
    // required tap area.
    <label
      className={cx(
        'flex items-start gap-3 min-h-tap py-2 cursor-pointer select-none',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        disabled={disabled}
        {...field}
        className={cx(
          // 18px square. accent-color is what stops these rendering in browser blue.
          'mt-0.5 h-[18px] w-[18px] shrink-0 rounded-ct-sm border border-line-control',
          'accent-[rgb(var(--ct-green-rgb))]',
          focusRing,
        )}
        {...rest}
      />
      <span className="flex flex-col gap-0.5">
        <span className="text-sm text-ink">{label}</span>
        {hint ? <span className="text-xs text-ink-3">{hint}</span> : null}
      </span>
    </label>
  );
});
